import React from 'react'
import clsx from 'clsx'
import styled from 'styled-components'
import { getThemepalette } from 'config/theme'
import { ReactComponent as DeleteIcon } from 'assets/img/remove-icon-bold.svg'
import HtmlParser from 'components/Assessment/HtmlParser'
import RightExpand from 'assets/icons/right-expand.svg'
import LeftExpand from 'assets/icons/left-expand.svg'
import {
  InstructionBanner,
  InstructionBannerContainer,
  InstructionText,
  TabsHeader,
  TabsGroup,
  TabButton,
  TabButtonContent,
  TabHeaderActionButton,
  TabHeaderActionIcon,
  ReadingScroll,
  ReadingPanel
} from './styles'

export type StimulusAreaMode = 'edit' | 'readonly'

/**
 * The shared stimulus shell should stay on the fixed light viewer palette,
 * while contrast classes continue to restyle the authored reading content.
 */
const lightContentTheme = getThemepalette('light')

const LightTabsHeader = styled(TabsHeader)`
  background: ${lightContentTheme.colors.surface.muted};
`

const LightTabsGroup = styled(TabsGroup)`
  border-bottom: 1px solid ${lightContentTheme.colors.border.primary};
`

const LightTabButton = styled(TabButton)`
  border-bottom: 1px solid ${lightContentTheme.colors.border.primary};
  background: ${(props) =>
    props.$active
      ? lightContentTheme.colors.surface.infoDark
      : lightContentTheme.colors.card.background};
  color: ${(props) =>
    props.$active ? '#fff' : lightContentTheme.colors.text.primary};
`

const LightInstructionBanner = styled(InstructionBanner)`
  background: ${lightContentTheme.colors.skeleton.primary};
  flex: 1;
`

const LightInstructionText = styled(InstructionText)`
  color: ${lightContentTheme.colors.text.info};

  p {
    color: ${lightContentTheme.colors.text.info};
  }
`

const AddTabHeader = styled.div`
  width: 100%;
  height: 60px;
  min-height: 60px;
  max-height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${lightContentTheme.colors.card.background};
  border-bottom: 1px solid ${lightContentTheme.colors.border.secondary};
`

const AddTabButton = styled.button`
  border: 1px solid ${lightContentTheme.colors.border.primary};
  border-radius: 8px;
  background: ${lightContentTheme.colors.card.background};
  color: ${lightContentTheme.colors.text.primary};
  font-family: ${(props) => props.theme.typography.fontNunito};
  font-size: 14px;
  height: 33px;
  padding: 0 14px;
  cursor: pointer;
`

const LockableReadingPanel = styled(ReadingPanel)`
  border-right: 1px solid ${lightContentTheme.colors.border.primary};
  position: relative;
`

const StimulusContentLayer = styled.div<{ $locked: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  opacity: ${(props) => (props.$locked ? 0.48 : 1)};
  transition: opacity 180ms ease;
`

const LockableReadingScroll = styled(ReadingScroll)`
  position: relative;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;

  .answers__editor-edit {
    margin-top: 0;
  }
`

const StimulusLockedOverlay = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(143, 148, 159, 0.3);
  z-index: 3;
  pointer-events: none;
`

const IconButton = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  padding: 0;
`

const SingleTabActionButton = styled(TabHeaderActionButton)`
  position: relative;
  right: auto;
  flex-shrink: 0;
  margin-left: auto;
`

const SingleTabActionSpacer = styled.div`
  display: flex;
  align-items: center;
  padding: 8px;
  background: #ffffff;
`
interface StimulusAreaProps {
  readOnly: boolean
  stimulusTabOne: string
  shouldRenderSecondTab?: boolean
  stimulusTabTwo: string
  isStimulusEditDisabled?: boolean
  activeTab: 'tab1' | 'tab2'
  setActiveTab: (tab: 'tab1' | 'tab2') => void
  onStimulusChange?: (
    tabKey: 'stimulus_tab_one' | 'stimulus_tab_two',
    value: string
  ) => void
  shouldShowTabsHeader?: boolean
  shouldShowAddTabHeader?: boolean
  onLeftArrowClick: () => void
  onRightArrowClick: () => void
  isLeftArrowDisabled?: boolean
  isRightArrowDisabled?: boolean
  onRemoveStimulusTabTwo?: () => void
  fullWidth?: boolean
  onAddStimulusTab?: () => void
  instructionHtml?: string
  showInstructionEditIcon?: boolean
  stimulusTabOneId?: string
  stimulusTabTwoId?: string
}
const StimulusArea: React.FC<StimulusAreaProps> = ({
  readOnly,
  stimulusTabOne,
  shouldRenderSecondTab,
  stimulusTabTwo,
  isStimulusEditDisabled = false,
  shouldShowTabsHeader,
  shouldShowAddTabHeader = false,
  onLeftArrowClick,
  onRightArrowClick,
  isLeftArrowDisabled = false,
  isRightArrowDisabled = false,
  onRemoveStimulusTabTwo,
  setActiveTab,
  activeTab,
  fullWidth,
  onAddStimulusTab,
  instructionHtml,
  stimulusTabOneId = 'stimulus_tab_one',
  stimulusTabTwoId = 'stimulus_tab_two'
}) => {
  /**
   * Assessments retain the teacher portal's passage ids for TTS metadata,
   * while assignments continue using their existing stimulus ids by default.
   */
  const firstStimulusId = stimulusTabOneId
  const secondStimulusId = stimulusTabTwoId
  const hasStimulusTabOneData = !!stimulusTabOne.trim()
  const hasStimulusTabTwoData = !!stimulusTabTwo.trim()
  const shouldRenderSecondTabResolved =
    shouldRenderSecondTab ?? hasStimulusTabTwoData
  const shouldShowTabsHeaderResolved =
    shouldShowTabsHeader ??
    (hasStimulusTabOneData && shouldRenderSecondTabResolved)

  /**
   * A single stimulus keeps the expand controls without showing tab chrome.
   * Place them beside the instruction content, with a fallback row when no
   * instruction html exists to provide the banner surface.
   */
  const shouldShowSingleTabActions =
    !shouldShowTabsHeaderResolved && !shouldShowAddTabHeader

  return (
    <LockableReadingPanel $fullWidth={fullWidth || false}>
      <StimulusContentLayer $locked={isStimulusEditDisabled}>
        {shouldShowTabsHeaderResolved && (
          <LightTabsHeader>
            <LightTabsGroup>
              <LightTabButton
                style={{
                  borderRight: `1px solid ${lightContentTheme.colors.border.primary}`
                }}
                type='button'
                className={clsx(
                  readOnly
                    ? activeTab === 'tab1'
                      ? 'contrast-area-secondary'
                      : 'contrast-area'
                    : null
                )}
                $active={activeTab === 'tab1'}
                onClick={() => setActiveTab('tab1')}
              >
                Tab 1
              </LightTabButton>
              <LightTabButton
                type='button'
                className={clsx(
                  readOnly
                    ? activeTab === 'tab2'
                      ? 'contrast-area-secondary'
                      : 'contrast-area'
                    : null
                )}
                $active={activeTab === 'tab2'}
                onClick={() => setActiveTab('tab2')}
              >
                <TabButtonContent>
                  <span>Tab 2</span>
                  {!readOnly && !isStimulusEditDisabled && (
                    <IconButton
                      role='button'
                      aria-label='Remove second stimulus tab'
                      style={{
                        position: 'absolute',
                        left: 20,
                        cursor: 'pointer'
                      }}
                      onClick={onRemoveStimulusTabTwo}
                    >
                      <DeleteIcon className='' />
                    </IconButton>
                  )}
                </TabButtonContent>
              </LightTabButton>
            </LightTabsGroup>

            <TabHeaderActionButton>
              <TabHeaderActionIcon
                $disabled={isRightArrowDisabled}
                aria-disabled={isRightArrowDisabled}
                onClick={isRightArrowDisabled ? undefined : onRightArrowClick}
                src={RightExpand}
                alt='maximize'
              />
              <TabHeaderActionIcon
                $disabled={isLeftArrowDisabled}
                aria-disabled={isLeftArrowDisabled}
                onClick={isLeftArrowDisabled ? undefined : onLeftArrowClick}
                src={LeftExpand}
                alt='maximize'
              />
            </TabHeaderActionButton>
          </LightTabsHeader>
        )}

        {shouldShowAddTabHeader && !isStimulusEditDisabled && (
          <AddTabHeader
            className={clsx({
              'contrast-area-secondary': readOnly
            })}
          >
            <AddTabButton
              type='button'
              onClick={onAddStimulusTab}
              disabled={isStimulusEditDisabled}
            >
              Add Tab
            </AddTabButton>

            <TabHeaderActionButton>
              <TabHeaderActionIcon
                src={RightExpand}
                $disabled={isRightArrowDisabled}
                aria-disabled={isRightArrowDisabled}
                onClick={isRightArrowDisabled ? undefined : onRightArrowClick}
                alt=''
                aria-hidden='true'
              />
              <TabHeaderActionIcon
                $disabled={isLeftArrowDisabled}
                aria-disabled={isLeftArrowDisabled}
                onClick={isLeftArrowDisabled ? undefined : onLeftArrowClick}
                src={LeftExpand}
                alt=''
                aria-hidden='true'
              />
            </TabHeaderActionButton>
          </AddTabHeader>
        )}

        <LockableReadingScroll className='contrast-area stimulus-content-area'>
          <div
            className='answers__editor'
            style={{
              height: 'auto',
              borderRight: 'none',
              padding: '10px 14px'
            }}
          >
            {instructionHtml && (
              <InstructionBannerContainer
                className={clsx({
                  'contrast-area-secondary': true
                })}
              >
                <LightInstructionBanner>
                  <LightInstructionText>
                    <HtmlParser
                      html={instructionHtml || 'passage_directions'}
                      id={'passage_directions'}
                    />
                  </LightInstructionText>
                </LightInstructionBanner>
                {shouldShowSingleTabActions && (
                  <SingleTabActionSpacer>
                    <SingleTabActionButton>
                      <TabHeaderActionIcon
                        $disabled={isRightArrowDisabled}
                        aria-disabled={isRightArrowDisabled}
                        onClick={
                          isRightArrowDisabled ? undefined : onRightArrowClick
                        }
                        src={RightExpand}
                        alt='Expand question panel'
                      />
                      <TabHeaderActionIcon
                        $disabled={isLeftArrowDisabled}
                        aria-disabled={isLeftArrowDisabled}
                        onClick={
                          isLeftArrowDisabled ? undefined : onLeftArrowClick
                        }
                        src={LeftExpand}
                        alt='Expand stimulus panel'
                      />
                    </SingleTabActionButton>
                  </SingleTabActionSpacer>
                )}
              </InstructionBannerContainer>
            )}

            {shouldShowSingleTabActions && !instructionHtml && (
              <SingleTabActionButton>
                <TabHeaderActionIcon
                  $disabled={isRightArrowDisabled}
                  aria-disabled={isRightArrowDisabled}
                  onClick={isRightArrowDisabled ? undefined : onRightArrowClick}
                  src={RightExpand}
                  alt='Expand question panel'
                />
                <TabHeaderActionIcon
                  $disabled={isLeftArrowDisabled}
                  aria-disabled={isLeftArrowDisabled}
                  onClick={isLeftArrowDisabled ? undefined : onLeftArrowClick}
                  src={LeftExpand}
                  alt='Expand stimulus panel'
                />
              </SingleTabActionButton>
            )}

            {/* State 1: Only one tab is available, so render Tab 1 content directly. */}
            {!shouldRenderSecondTabResolved && hasStimulusTabOneData && (
              <div className={clsx('answers__editor-edit')}>
                {readOnly || isStimulusEditDisabled ? (
                  <HtmlParser html={stimulusTabOne} id={firstStimulusId} />
                ) : null}
              </div>
            )}
            {/* State 2: Tab 1 is empty, but Tab 2 exists, so fallback to Tab 2 content. */}
            {!hasStimulusTabOneData && shouldRenderSecondTabResolved && (
              <div className={clsx('answers__editor-edit')}>
                {readOnly || isStimulusEditDisabled ? (
                  <HtmlParser html={stimulusTabTwo} id={secondStimulusId} />
                ) : null}
              </div>
            )}
            {/* State 3: Two-tab mode is enabled and the user selected Tab 1. */}
            {shouldRenderSecondTabResolved &&
              hasStimulusTabOneData &&
              activeTab === 'tab1' && (
                <div className={clsx('answers__editor-edit')}>
                  {readOnly || isStimulusEditDisabled ? (
                    <HtmlParser html={stimulusTabOne} id={firstStimulusId} />
                  ) : null}
                </div>
              )}
            {/* State 4: Two-tab mode is enabled and the user selected Tab 2. */}
            {shouldRenderSecondTabResolved && activeTab === 'tab2' && (
              <div className={clsx('answers__editor-edit')}>
                {readOnly || isStimulusEditDisabled ? (
                  <HtmlParser html={stimulusTabTwo} id={secondStimulusId} />
                ) : null}
              </div>
            )}
          </div>
        </LockableReadingScroll>
      </StimulusContentLayer>

      {isStimulusEditDisabled && <StimulusLockedOverlay />}
    </LockableReadingPanel>
  )
}

export default StimulusArea
